import { MarkEmailRead } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { IBookingRequestAccepted, IBookingRequestSubmission } from '../../../library/models/publicBooking';
import { splitApiError } from '../../../library/useApiFormError';
import ResourcesIds from '../../../resources/ResourcesIds';
import { useReactMutation } from '../../../services/queryService';
import { services } from '../../../services/services';
import { useSnackbar } from '../../layout/snackbarProvider';
import { actionsStyles, bodyStyles, iconStyles, panelStyles, titleStyles } from './checkEmail.styles';

/** How long the emailed link works -- the API's `OnlineBookingRequestService.LINK_LIFETIME`. */
const LINK_LIFETIME_HOURS: number = 24;

interface CheckEmailProps {
    submission: IBookingRequestSubmission;
    onResent: (submission: IBookingRequestSubmission) => void;
    onChangeDetails: () => void;
}

/**
 * What a visitor sees once the request has gone: the same words whatever the API found, since
 * it answers every submission alike -- "if this is a valid address". A known address is sent
 * the wording for an existing account and nothing on screen says so.
 *
 * *Send it again* resubmits the same request, which retires the earlier link: only the newest
 * works, so the one they eventually open is never for a time they have since abandoned.
 */
export const CheckEmail = ({ submission, onResent, onChangeDetails }: CheckEmailProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const { showSnackbar } = useSnackbar();

    const resend = useReactMutation<IBookingRequestAccepted, Error, IBookingRequestSubmission>(
        services.publicBooking.submitRequestMutation()
    );

    const onResend = (): void => {
        resend.mutate(submission, {
            onSuccess: () => {
                onResent(submission);
                showSnackbar(translate(ResourcesIds.PUBLIC_BOOKING_CHECK_EMAIL_RESENT), 'success');
            },
            onError: (error: unknown) => showSnackbar(
                splitApiError(error, [], translate, ResourcesIds.PUBLIC_BOOKING_SUBMIT_FAILED).formError
                ?? translate(ResourcesIds.PUBLIC_BOOKING_SUBMIT_FAILED),
                'error'
            ),
        });
    };

    return (
        <Box sx={panelStyles}>
            <MarkEmailRead sx={iconStyles} />
            <Typography variant="h4" component="h1" sx={titleStyles}>
                {translate(ResourcesIds.PUBLIC_BOOKING_CHECK_EMAIL_TITLE)}
            </Typography>
            <Typography sx={bodyStyles}>
                {translate(ResourcesIds.PUBLIC_BOOKING_CHECK_EMAIL_BODY, {
                    email: submission.emailAddress,
                    hours: LINK_LIFETIME_HOURS,
                })}
            </Typography>
            <Box sx={actionsStyles}>
                <Button variant="outlined" onClick={onResend} disabled={resend.isPending}>
                    {translate(ResourcesIds.PUBLIC_BOOKING_CHECK_EMAIL_RESEND)}
                </Button>
                <Button onClick={onChangeDetails}>
                    {translate(ResourcesIds.PUBLIC_BOOKING_CHECK_EMAIL_CHANGE)}
                </Button>
            </Box>
        </Box>
    );
};
